import { Chip, ChipProps, SvgIcon } from '@mui/material'
import React, { useMemo } from 'react'

interface LabelChipProps extends Omit<ChipProps, 'icon'> {
  icon?: React.ReactNode | React.ComponentType
  full?: boolean
}

const LabelChip = ({
  icon,
  color = 'primary',
  label = '',
  variant = 'filled',
  size = 'small',
  full = false,
  sx,
  ...rest
}: LabelChipProps) => {
  const iconElement = useMemo(() => {
    if (!icon) return undefined

    // Se já é um elemento React válido, retorna direto
    if (React.isValidElement(icon)) {
      return icon
    }

    // Se for um componente (função, classe, memo, forwardRef, etc), usa SvgIcon
    if (typeof icon === 'function' || typeof icon === 'object') {
      return <SvgIcon component={icon as React.ElementType} />
    }

    // Para outros tipos (string, number, etc), envolve em um span
    return <span>{icon}</span>
  }, [icon])

  const chipStyles = useMemo(
    () => ({
      width: full ? '100%' : 'fit-content',
      padding: '0px 8px',
      height: 24,
      display: 'flex',
      gap: 1,
      alignItems: 'center',
      '& .MuiChip-icon': { margin: 0, fontSize: 16, color: 'inherit' },
      '& .MuiSvgIcon-root': { margin: 0, fontSize: 16, color: 'inherit' },
      '& .MuiChip-label': { padding: 0, fontSize: 14, color: 'inherit', lineHeight: '143%' },

      color: `var(--${color}-700)`,
      backgroundColor: `var(--${color}-50)`,
      borderColor: variant === 'outlined' ? `var(--${color}-500)` : 'transparent',
      borderWidth: variant === 'outlined' ? 1 : 0,

      ...sx
    }),
    [color, variant, full, sx]
  )

  return (
    <Chip
      icon={iconElement}
      label={label}
      size={size}
      variant={variant}
      sx={chipStyles}
      {...rest}
    />
  )
}

export default LabelChip
